// @flow

import React, { useState } from 'react';
import _ from 'underscore';

import type { Props } from './dropdown_select/types';
import {
  getNoResultsText,
  getLoadingPlaceholder,
  enhanceProps,
} from './dropdown_select/utils';

import { getComponent } from './dropdown_select/components';

const defaultProps = {
  isSearchable: false,
  isClearable: false,
  isCreatable: false,
  isMulti: false,
  isLazyLoad: false,
  isVirtualized: false,
  isTiered: false,
  isDisabled: false,
  hideDisabledOptions: false,
  options: [],
  placeholder: 'Select...',
  debounceTime: 500,
};

function debounce(fn, interval) {
  let timer = null;

  return (...args) => {
    if (timer) {
      clearTimeout(timer);
    }

    return new Promise((resolve) => {
      timer = setTimeout(() => resolve(fn(...args)), interval);
    });
  };
}

// **********
// Components
// **********
const Dropdown = (props: Props) => {
  if ((props.isTiered || props.isVirtualized) && props.isCreatable) {
    throw 'Virtualized and Tiered dropdowns cannot be Creatable. That functionality has not been implemented due to using legacy code';
  }

  const DropdownComponent = getComponent(props);

  const [options, setOptions] = useState(
    _.isFunction(props.options) ? [] : props.options
  );
  const [prevGetOptions, setPrevGetOptions] = useState(
    _.isFunction(props.options) ? () => props.options : props.options
  );
  const [isLoading, setIsLoading] = useState(false);
  const [menuIsOpen, setMenuIsOpen] = useState(false);

  const didArrayOptionsChange = () => {
    return Array.isArray(props.options) && props.options !== prevGetOptions;
  };

  const didFunctionOptionsChange = () => {
    return (
      props.options &&
      prevGetOptions &&
      props.options.toString() !== prevGetOptions.toString()
    );
  };

  if (props.isTiered || props.isVirtualized || props.isLazyLoad) {
    // handle async/lazy loading

    // Reset options and loading state if we get a new function for fetching options
    if (
      !prevGetOptions ||
      didArrayOptionsChange() ||
      didFunctionOptionsChange()
    ) {
      setIsLoading(false);
      if (typeof props.options === 'function') {
        setOptions([]);
        // in hooks, to save a function in state it must be wrapped like this because
        // useState setter treats a function argument as lazy calculation
        setPrevGetOptions(() => props.options);
      } else {
        setOptions(props.options);
        setPrevGetOptions(props.options);
      }
    }

    const fetchOptionsIfNeeded = () => {
      if (_.isFunction(props.options) && options.length === 0 && !isLoading) {
        setIsLoading(true);
        props.options('').then((data) => {
          setOptions(data);
          setIsLoading(false);
        });
      }
    };

    if (!props.isLazyLoad) {
      fetchOptionsIfNeeded();
    }

    if (props.isTiered || props.isVirtualized) {
      return (
        <DropdownComponent
          {...enhanceProps(props)}
          options={options}
          isLoading={isLoading}
          onOpen={fetchOptionsIfNeeded}
          noResultsText={getNoResultsText(props.noOptionsMessage)}
          loadingPlaceholder={getLoadingPlaceholder(props.loadingMessage)}
        />
      );
    }

    const handleMenuOpen = () => {
      fetchOptionsIfNeeded();
      setMenuIsOpen(true);
      props.onMenuOpen && props.onMenuOpen();
    };

    const handleMenuClose = () => {
      setMenuIsOpen(false);
      props.onMenuClose && props.onMenuClose();
    };

    return (
      <DropdownComponent
        {...enhanceProps(props)}
        isLoading={isLoading}
        menuIsOpen={menuIsOpen}
        onMenuOpen={handleMenuOpen}
        onMenuClose={handleMenuClose}
        options={options}
      />
    );
  } else if (_.isFunction(props.options)) {
    return (
      <DropdownComponent
        {...enhanceProps(props)}
        loadOptions={debounce(props.options, props.debounceTime)}
        defaultOptions
      />
    );
  }

  return <DropdownComponent {...enhanceProps(props)} options={props.options} />;
};
Dropdown.defaultProps = defaultProps;
export default Dropdown;
